import React from 'react';
import {
    Box,
    Typography,
    Checkbox,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from '@mui/material';
import { Spa, Grass, Agriculture } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

export interface SeasonalityData {
    plantIndoor?: { [key: number]: boolean };
    plantOutdoors?: { [key: number]: boolean };
    harvest?: { [key: number]: boolean };
}

interface SeasonalityInputProps {
    value: SeasonalityData;
    onChange: (newValue: SeasonalityData) => void;
}

const SeasonalityInput: React.FC<SeasonalityInputProps> = ({ value, onChange }) => {
    const { t } = useTranslation();
    // val is stricture: { plantIndoor: {1: bool...}, plantOutdoors: {...}, harvest: {...} }
    const data = value || { plantIndoor: {}, plantOutdoors: {}, harvest: {} };

    const months = [
        { num: 1, name: t('common.months.jan') }, { num: 2, name: t('common.months.feb') }, { num: 3, name: t('common.months.mar') },
        { num: 4, name: t('common.months.apr') }, { num: 5, name: t('common.months.may') }, { num: 6, name: t('common.months.jun') },
        { num: 7, name: t('common.months.jul') }, { num: 8, name: t('common.months.aug') }, { num: 9, name: t('common.months.sep') },
        { num: 10, name: t('common.months.oct') }, { num: 11, name: t('common.months.nov') }, { num: 12, name: t('common.months.dec') }
    ];

    const categories: { key: keyof SeasonalityData; label: string; icon: React.ReactNode }[] = [
        { key: 'plantIndoor', label: t('plants.indoor'), icon: <Spa fontSize="small" sx={{ color: 'var(--color-season-indoor)' }} /> },
        { key: 'plantOutdoors', label: t('plants.outdoor'), icon: <Grass fontSize="small" sx={{ color: 'var(--color-season-outdoor)' }} /> },
        { key: 'harvest', label: t('plants.harvest'), icon: <Agriculture fontSize="small" sx={{ color: 'var(--color-season-harvest)' }} /> },
    ];

    const handleToggle = (categoryKey: keyof SeasonalityData, monthNum: number) => {
        // Safe access with fallback empty object
        const currentCategoryMap = data[categoryKey] || {};
        // Toggle value
        const newValue = !currentCategoryMap[monthNum];

        onChange({
            ...data,
            [categoryKey]: {
                ...currentCategoryMap,
                [monthNum]: newValue
            }
        });
    };

    return (
        <Box sx={{ border: '1px solid rgba(0,0,0,0.12)', borderRadius: 1, overflow: 'hidden' }}>
            <TableContainer>
                <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 450 }}>
                    <TableHead>
                        <TableRow sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
                            <TableCell sx={{ width: '120px', fontWeight: 'bold' }}>{t('plants.activity')}</TableCell>
                            {months.map(m => (
                                <TableCell key={m.num} align="center" padding="none" sx={{ fontSize: '0.75rem', fontWeight: 'bold', py: 1 }}>
                                    {m.name}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {categories.map(cat => (
                            <TableRow key={cat.key}>
                                <TableCell component="th" scope="row">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {cat.icon}
                                        <Typography variant="body2">{cat.label}</Typography>
                                    </Box>
                                </TableCell>
                                {months.map(m => (
                                    <TableCell key={m.num} align="center" padding="none">
                                        <Checkbox
                                            size="small"
                                            checked={!!(data[cat.key] && data[cat.key]![m.num])}
                                            onChange={() => handleToggle(cat.key, m.num)}
                                            sx={{
                                                p: 0.5,
                                                '& .MuiSvgIcon-root': { fontSize: 16 },
                                                color: `var(--color-season-${cat.key === 'plantIndoor' ? 'indoor' : cat.key === 'plantOutdoors' ? 'outdoor' : 'harvest'})`,
                                                '&.Mui-checked': {
                                                    color: `var(--color-season-${cat.key === 'plantIndoor' ? 'indoor' : cat.key === 'plantOutdoors' ? 'outdoor' : 'harvest'})`,
                                                }
                                            }}
                                        />
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default SeasonalityInput;
